import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useDeclarationStats } from "../../../../../api/declarationsApi"

/**
 * The top bar's mini progress bar (prototype: `declProgress()` / `.dprog`) —
 * how much of the inbox is still waiting, while you work through it.
 *
 * WHERE THE NUMBERS COME FROM, AND WHY NOT THE PROTOTYPE'S. The prototype
 * counts a SESSION: what this reviewer resolved since the page loaded
 * ("N von M bestätigt", green for adopted, grey for requested/deferred). This
 * app persists no such session, and no lifecycle state records "requested" or
 * "deferred" either, so those two segments have nothing behind them. What the
 * server does count, over EVERY declaration rather than the loaded page, is
 * `useDeclarationStats` — the same read the inbox tiles use, so this costs no
 * new request on a warm cache.
 *
 * So the label states the one thing the data supports exactly ("noch X von Y
 * zu prüfen") and the bar fills the settled share, keeping the prototype's
 * two-segment grammar: green for what is done with, muted for what the
 * pipeline is still processing, and the unfilled remainder for what is waiting
 * on the reviewer. Deliberately no invented "confirmed" count: a number in a
 * compliance tool has to be one someone can stand behind.
 */
export function DeclProgressBar() {
  const { translate } = useTranslation()
  const { data: stats } = useDeclarationStats()

  const total = stats?.total ?? 0
  if (!stats || total <= 0) {
    return null
  }
  const awaiting = stats.awaitingReview
  const inProgress = stats.inProgress
  // Everything neither waiting on a reviewer nor still being extracted.
  const settled = Math.max(0, total - awaiting - inProgress)
  const pct = (n: number) => `${(n / total) * 100}%`

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="sv2-dprog" data-testid="decl-progress">
            <span className="sv2-dpl">
              {translate("MC_DECL_V2_PROGRESS_LABEL", { awaiting, total })}
            </span>
            <span className="sv2-dpb">
              <i className="sv2-a" style={{ width: pct(settled) }} />
              <i className="sv2-o" style={{ width: pct(inProgress) }} />
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          {translate("MC_DECL_V2_PROGRESS_TITLE", {
            settled,
            inProgress,
            awaiting,
          })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
